import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/LandingPage.css';
import { DottedSurface } from './ui/DottedSurface';
import { Sparkles } from './ui/sparkles';
import PublicNav from './PublicNav';

export default function PricingPage() {
  return (
    <div className="landing-page">
      <PublicNav />

      {/* ─── Sparkles Background ─── */}
      <div className="absolute top-0 left-0 right-0 h-96 overflow-hidden pointer-events-none" style={{ maskImage: 'radial-gradient(50% 50%, white, transparent)' }}>
        <Sparkles
          density={1200}
          direction="bottom"
          speed={0.8}
          color="#FFFFFF"
          className="absolute inset-x-0 bottom-0 h-full w-full"
          opacity={0.6}
        />
      </div>

      {/* ─── Pricing Hero ─── */}
      <section className="pricing-hero relative z-10">
        <div className="landing-section-badge">Pricing</div>

        <h1>
          Simple, Transparent Pricing
        </h1>

        <p>
          Start with guided setup. Billing is finalized before paid use.
        </p>
      </section>

      {/* ─── Pricing Cards ─── */}
      <section className="pricing-cards-section relative z-10">
        <div className="landing-pricing-grid">
          <div className="landing-pricing-card">
            <div className="landing-pricing-card-header">
              <div className="landing-pricing-tier-name">Professional</div>
              <div className="landing-pricing-price">$99/month</div>
              <div className="landing-pricing-desc">Guided setup · Billing finalized before paid use</div>
            </div>
            <ul className="landing-pricing-features">
              <li><span className="check">✓</span> Up to 5 scrubs/month</li>
              <li><span className="check">✓</span> AI email + extraction</li>
              <li><span className="check">✓</span> Google Sheets sync</li>
              <li><span className="check">✓</span> PDF downloads</li>
              <li><span className="check">✓</span> Smart follow-ups</li>
            </ul>
            <Link to="/login" className="landing-pricing-btn">Start Setup</Link>
          </div>

          <div className="landing-pricing-card featured">
            <div className="landing-pricing-card-header">
              <div className="landing-pricing-tier-name">Researcher</div>
              <div className="landing-pricing-price">$199/month</div>
              <div className="landing-pricing-desc">Guided setup · Billing finalized before paid use</div>
            </div>
            <ul className="landing-pricing-features">
              <li><span className="check">✓</span> Up to 20 scrubs/month</li>
              <li><span className="check">✓</span> Smart follow-ups</li>
              <li><span className="check">✓</span> Tour & call detection</li>
              <li><span className="check">✓</span> Priority support</li>
              <li><span className="check">✓</span> Custom email templates</li>
              <li><span className="check">✓</span> Everything in Professional</li>
            </ul>
            <Link to="/login" className="landing-pricing-btn featured">Start Setup</Link>
          </div>

          <div className="landing-pricing-card">
            <div className="landing-pricing-card-header">
              <div className="landing-pricing-tier-name">Enterprise</div>
              <div className="landing-pricing-price">
                <span className="landing-pricing-amount" style={{ fontSize: '1.5rem' }}>Call for a custom quote</span>
              </div>
              <div className="landing-pricing-desc">Unlimited scrubs · Priced to your volume</div>
            </div>
            <ul className="landing-pricing-features">
              <li><span className="check">✓</span> Unlimited scrubs/month</li>
              <li><span className="check">✓</span> Multi-user access</li>
              <li><span className="check">✓</span> Team analytics</li>
              <li><span className="check">✓</span> Custom integrations</li>
              <li><span className="check">✓</span> Dedicated support</li>
              <li><span className="check">✓</span> Everything in Researcher</li>
            </ul>
            <a href="mailto:hello@sitesiftai.com" className="landing-pricing-btn">Contact Sales</a>
          </div>
        </div>
      </section>

      {/* ─── FAQ Section ─── */}
      <section className="pricing-faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="pricing-faq-grid">
          <div className="pricing-faq-item">
            <h3>How does setup work?</h3>
            <p>Start setup by signing in with Microsoft so SiteSift can connect Outlook and prepare your workspace. We finalize billing before paid use begins.</p>
          </div>
          <div className="pricing-faq-item">
            <h3>Can I cancel anytime?</h3>
            <p>Yes, you can cancel your subscription at any time. No long-term contracts or hidden fees.</p>
          </div>
          <div className="pricing-faq-item">
            <h3>What counts as a "scrub"?</h3>
            <p>One scrub is one campaign — one uploaded spreadsheet of properties that the AI emails, monitors, and reports on through to completion. Each property within a scrub is handled automatically end-to-end.</p>
          </div>
          <div className="pricing-faq-item">
            <h3>Do you offer discounts for teams?</h3>
            <p>Yes! Contact us for custom pricing tailored to your brokerage's needs.</p>
          </div>
        </div>
      </section>

      {/* ─── Footer with CTA ─── */}
      <footer className="relative overflow-hidden" style={{ background: 'transparent', minHeight: '77vh' }}>
        {/* Animated dots - contained within footer */}
        <DottedSurface theme="dark" />

        {/* Content */}
        <div className="relative z-10 text-center pt-24 pb-8">
          <div className="max-w-xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to <span style={{ background: 'linear-gradient(135deg, #3b82f6, #22d3ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Automate</span> Your Broker Outreach?
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Join brokers who are saving hours every week with AI-powered email automation.
            </p>
            <Link to="/login" className="landing-cta-btn">
              Start Setup
            </Link>
            <div className="text-gray-500 text-sm mt-6">
              Works with Outlook + Google Sheets
            </div>
          </div>
        </div>

        {/* Copyright - positioned at bottom */}
        <div className="absolute bottom-8 left-0 right-0 text-center text-gray-500 text-sm z-10">
          <p>&copy; {new Date().getFullYear()} SiteSift.AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
